import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  HashIcon,
  IconComponent,
  ListIcon,
  RupeeIcon,
  TypeIcon,
  UploadCloudIcon,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import {
  EmployeeFieldAccess,
  EmployeeFieldValueType,
  FieldPreviewState,
  FieldTypeMeta,
} from './types';

export const employeeDataTabs = {
  general: 'general',
  fields: 'fields',
} as const;

export const getEmployeeDataTabItems = () => [
  {
    children: 'General',
    value: employeeDataTabs.general,
    path: routePaths.settings.employeeData.general,
  },
  {
    children: 'Employee Fields',
    value: employeeDataTabs.fields,
    path: routePaths.settings.employeeData.fields,
  },
];

export const FIELD_TYPE_OPTIONS: Array<{
  label: string;
  value: EmployeeFieldValueType;
  helperText: string;
}> = [
  { label: 'Text', value: 'STRING', helperText: 'Words, names, IDs, or short notes.' },
  { label: 'Number', value: 'INTEGER', helperText: 'Whole numbers only, e.g. “12”.' },
  { label: 'Decimal', value: 'DECIMAL', helperText: 'Numbers with decimal values, e.g. “8.5”.' },
  { label: 'Currency (₹)', value: 'CURRENCY', helperText: 'Enter rupee amounts, e.g. “₹50,000”.' },
  { label: 'Date', value: 'DATE', helperText: 'Select a calendar date, e.g. “15 Jun 2026”.' },
  { label: 'Yes/No', value: 'BOOLEAN', helperText: 'Choose either “Yes” or “No”.' },
  { label: 'Dropdown list', value: 'ENUM', helperText: 'Choose from a list of options' },
  { label: 'File Upload', value: 'FILE', helperText: 'Attach a document or image.' },
];

// FILE stays in FIELD_TYPE_OPTIONS so existing file fields keep their label in
// the listing and edit flows, but it is not offered for new fields yet.
export const SELECTABLE_FIELD_TYPE_OPTIONS = FIELD_TYPE_OPTIONS.filter(
  (option) => option.value !== 'FILE',
);

export const FIELD_TYPE_META: Record<EmployeeFieldValueType, FieldTypeMeta> = {
  STRING: {
    Icon: TypeIcon,
    helperText: 'Words, names, IDs, or short notes.',
    selectedHelperText: 'Words, names, IDs, or short notes.',
  },
  INTEGER: {
    Icon: HashIcon,
    helperText: 'Whole numbers only, e.g. “12”.',
    selectedHelperText: 'Whole numbers only, e.g. “12”.',
  },
  DECIMAL: {
    Icon: HashIcon,
    helperText: 'Numbers with decimal values, e.g. “8.5”.',
    selectedHelperText: 'Numbers with decimal values, e.g. “8.5”.',
  },
  CURRENCY: {
    Icon: RupeeIcon,
    helperText: 'Enter rupee amounts, e.g. “₹50,000”.',
    selectedHelperText: 'Enter rupee amounts, e.g. “₹50,000”.',
  },
  DATE: {
    Icon: CalendarIcon,
    helperText: 'Select a calendar date, e.g. “15 Jun 2026”.',
    selectedHelperText: 'Select a calendar date, e.g. “15 Jun 2026”.',
  },
  BOOLEAN: {
    Icon: EyeIcon,
    helperText: 'Choose either “Yes” or “No”.',
    selectedHelperText: 'Choose either “Yes” or “No”.',
  },
  ENUM: {
    Icon: ListIcon,
    helperText: 'Choose from a list of options',
    selectedHelperText: 'Choose from a list of options',
  },
  FILE: {
    Icon: UploadCloudIcon,
    helperText: 'Attach a document or image.',
    selectedHelperText: 'Attach a document or image.',
  },
};

export const ACCESS_LABELS: Record<EmployeeFieldAccess, string> = {
  EDIT: 'Edit',
  VIEW_ONLY: 'View only',
  HIDDEN: 'Hidden',
};

export const ACCESS_META: Record<EmployeeFieldAccess, { Icon: IconComponent }> = {
  EDIT: { Icon: EditIcon },
  VIEW_ONLY: { Icon: EyeIcon },
  HIDDEN: { Icon: EyeOffIcon },
};

export const ADD_FIELD_WIZARD_STEPS = ['Field details', 'Permissions', 'Review'];

export const EMPTY_FIELD_PREVIEW: FieldPreviewState = {
  textValue: '',
  dateValue: null,
  booleanValue: '',
};

/** Number of placeholder panels rendered while the General tab settings load. */
export const GENERAL_SETTINGS_SKELETON_PANEL_COUNT = 3;

/** Number of placeholder rows rendered while the employee fields table loads. */
export const EMPLOYEE_FIELDS_SKELETON_ROW_COUNT = 6;
